import React from 'react'
import {
  useMoteRegisterMutation,
  useMoteAddChunkMutation,
  useMoteFinalizeMutation,
  moteRegistrationDataGetter,
  moteFinaliseDataGetter,
  useMoteRecordPauseMutation
} from '@mote/api'
import { Diagnostics, DiagnosticsLogger } from '@mote/helpers/diagnostics'
import { useFlightRecorder } from '../state/useFlightRecorder'

const logD = DiagnosticsLogger('useMediaUploader', 'Media uploader')

export const useMediaUploader = (parentMoteId, shouldRedirect) => {
  const [chunksToSend, setChunksToSend] = React.useState([])
  const [otherError, setOtherError] = React.useState(null)

  const {
    flightState,
    assignMoteId,
    registerRequest,
    deregisterRequest,
    increaseChunksErrorCount,
    checkStatusAndUpdateRationing
  } = useFlightRecorder()

  const {
    data,
    error: registerError,
    mutate: registerMote,
    flush: flushRegistrationData
  } = useMoteRegisterMutation(parentMoteId, shouldRedirect)

  const { mutate: pauseRecording } = useMoteRecordPauseMutation()

  const { mutate: addChunk, flush: flushChunkData } = useMoteAddChunkMutation(
    flightState.moteId,
    0
  )

  const {
    data: finalizeData,
    error: finalizeError,
    mutate: finalizeMote,
    flush: flushFinalizationData
  } = useMoteFinalizeMutation(flightState.moteId)

  const [chunksThresholdError, setChunksThresholdError] = React.useState(0)

  React.useEffect(() => {
    if (
      chunksThresholdError ||
      flightState.chunksErrorCount < flightState.chunksErrorThreshold
    )
      return null

    logD.error(
      `Chunks error threshold (of ${flightState.chunksErrorThreshold}) reached`
    )

    setChunksThresholdError({ name: 'CHUNKS_ERROR_THRESHOLD_ERROR' })
  }, [flightState.chunksErrorCount, flightState.chunksErrorThreshold])

  async function register() {
    await flushOldData()
    registerRequest('m/register')

    registerMote().then(response => {
      const responseData = moteRegistrationDataGetter(response.data)
      if (!response.error && responseData?.moteId) {
        assignMoteId(responseData.moteId)
      }
      response.error
        ? logD.error('Mote registration error', { error: response.error })
        : logD.info(
            `Mote registered successfully ID: ${responseData?.moteId}`,
            {
              moteId: responseData?.moteId
            }
          )
    })
  }

  function sendChunk(chunk) {
    setChunksToSend(prevChunks => {
      return [
        ...prevChunks,
        {
          data: chunk,
          id: prevChunks.length
        }
      ]
    })
  }

  function send(chunk) {
    if (!flightState?.moteId) return

    const chunkSize = chunk.data.size
    const chunkRoute = `m/addChunk/${flightState.moteId}/${chunk.id}?size=${chunkSize}`
    registerRequest(chunkRoute)
    addChunk({
      body: chunk.data,
      abortable: false,
      routeOverride: chunkRoute
    }).then(response => {
      if (response.error) {
        increaseChunksErrorCount(chunkRoute)
      } else {
        deregisterRequest(chunkRoute)
      }

      if (!response.error) return
      logD.error(
        `Chunk ID: ${chunk.id} with mote ID: ${flightState.moteId} failed to be sent`,
        { error: response.error }
      )
    })

    markSentChunk(chunk)
  }

  function markSentChunk(chunk) {
    setChunksToSend(prevChunks =>
      prevChunks.map(item =>
        item.id === chunk.id ? { ...item, sent: true } : item
      )
    )
  }

  function pause() {
    pauseRecording({
      variables: {
        moteId: flightState.moteId,
        latestChunk: chunksToSend.length
      }
    })
  }

  async function finalize(additionalPayload = {}) {
    if (!flightState.moteId) {
      logD.error('Called /finalize but ID is undefined')

      setOtherError({ name: 'MOTE_ID_UNDEFINED_ERROR' })
      return
    }
    const bytes = chunksToSend.reduce((total, chunk) => {
      return total + chunk.data.size
    }, 0)

    const response = await finalizeMote({
      variables: {
        summary: { chunks: chunksToSend.length, bytes },
        ...additionalPayload
      }
    })

    checkStatusAndUpdateRationing()

    response.error
      ? logD.error('Mote finalization failed', {
          error: response.error
        })
      : logD.info('Mote finalized successfully', {
          chunksTotal: chunksToSend.length,
          bytesTotal: bytes
        })

    if (response.error) {
      console.error('Finalize error', response.error)

      Diagnostics.dispatch({
        userVisibleErrorMessage: null,
        dispatchContext: 'USE_MEDIA_UPLOADER',
        title: 'Mote finalize error'
      })

      return response
    }

    cleanup()

    return response
  }

  function cleanup(options = {}) {
    setChunksToSend([])
    setChunksThresholdError(0)
    setOtherError(null)
    if (options.flushOldData) {
      flushOldData()
    }
  }

  async function flushOldData() {
    flushRegistrationData()
    flushFinalizationData()
    flushChunkData()
  }

  React.useEffect(() => {
    if (!flightState.moteId) return

    chunksToSend.forEach(chunk => {
      if (chunk.sent) return
      send(chunk)
    })
  }, [flightState.moteId, chunksToSend])

  return {
    registeredMote: moteRegistrationDataGetter(data),
    register,
    sendChunk,
    finalize,
    pause,
    finalizeData: moteFinaliseDataGetter(finalizeData),
    cleanup,
    error: registerError || finalizeError || chunksThresholdError || otherError
  }
}
